import { useRef, useState } from "react";
import { Mic, Send, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

function fmt(s) {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function VoiceRecorderButton({ dark = false, disabled = false, onRecorded }) {
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const discardRef = useRef(false);
  const startedAtRef = useRef(0);

  const start = async () => {
    if (disabled || busy || recording) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Sprachaufnahme wird von diesem Browser nicht unterstützt");
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error("Mikrofon-Zugriff verweigert — bitte in den Einstellungen erlauben");
      return;
    }
    const mime = MediaRecorder.isTypeSupported?.("audio/mp4")
      ? "audio/mp4"
      : MediaRecorder.isTypeSupported?.("audio/webm")
        ? "audio/webm"
        : "";
    const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    chunksRef.current = [];
    discardRef.current = false;
    mr.ondataavailable = (e) => {
      if (e.data?.size) chunksRef.current.push(e.data);
    };
    mr.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      clearInterval(timerRef.current);
      const duration = Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000));
      setRecording(false);
      setSecs(0);
      if (discardRef.current || !chunksRef.current.length) return;
      const type = mr.mimeType || mime || "audio/webm";
      const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "weba";
      const file = new File([new Blob(chunksRef.current, { type })], `Sprachnachricht.${ext}`, { type });
      setBusy(true);
      try {
        await onRecorded(file, duration);
      } finally {
        setBusy(false);
      }
    };
    recRef.current = mr;
    startedAtRef.current = Date.now();
    mr.start(250);
    setRecording(true);
    setSecs(0);
    timerRef.current = setInterval(() => setSecs((s) => s + 1), 1000);
  };

  const stop = (discard) => {
    discardRef.current = discard;
    try {
      recRef.current?.stop();
    } catch {
      /* noop */
    }
  };

  return (
    <>
      {recording && (
        <div
          data-testid="chat-voice-bar"
          className={`absolute bottom-full left-3 right-3 mb-2 z-30 flex items-center gap-3 rounded-2xl px-3.5 py-2.5 shadow-xl border ${
            dark ? "bg-neutral-800 border-neutral-700 text-white" : "bg-white border-neutral-200 text-neutral-800"
          }`}
        >
          <span className="h-2.5 w-2.5 rounded-full bg-rose-500 animate-pulse shrink-0" />
          <span className="text-sm font-bold tabular-nums" data-testid="chat-voice-timer">{fmt(secs)}</span>
          <span className={`flex-1 text-xs ${dark ? "text-neutral-400" : "text-neutral-500"}`}>Aufnahme läuft…</span>
          <button
            data-testid="chat-voice-discard"
            onClick={() => stop(true)}
            title="Verwerfen"
            className={`h-9 w-9 rounded-full flex items-center justify-center ${dark ? "text-neutral-300 hover:bg-neutral-700" : "text-neutral-500 hover:bg-neutral-100"}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            data-testid="chat-voice-send"
            onClick={() => stop(false)}
            title="Senden"
            className="h-9 w-9 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      )}
      <button
        data-testid="chat-voice-btn"
        onClick={() => (recording ? stop(false) : start())}
        disabled={disabled || busy}
        title={recording ? "Aufnahme senden" : "Sprachnachricht aufnehmen"}
        className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 transition ${
          recording ? "bg-rose-500 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"
        } disabled:opacity-40`}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Send className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
      </button>
    </>
  );
}
